import { FC } from 'react';

import { Box, Loader } from '@common/components';
import { LARGE_DATE_FORMAT, formatDate } from '@common/utils';

import { BLANK_VALUE, GENETIC_COUNSELING_TRANSLATION } from '../../constants';
import { getAppointmentTime } from '../../utils';
import { PatientProfileOverviewProps } from './PatientProfileOverview.types';
import { PatientProfileResponse, ReferralStatuses, isCompletedStatus } from '../../types';

import AppointmentCompletedCard from '../AppointmentCompletedCard/AppointmentCompletedCard';
import PatientDeclinedCard from '../shared/PatientDeclinedCard/PatientDeclinedCard';
import PatientNoShowCard from '../shared/PatientNoShowCard/PatientNoShowCard';
import UpcomingAppointmentCard from '../shared/UpcomingAppointmentCard/UpcomingAppointmentCard';
import PatientContactAttemptedCard from '../PatientContactAttemptedCard/PatientContactAttemptedCard';
import { useTranslation } from 'react-i18next';
import { useFeatureFlags } from '@modules/feature-flags/hooks';
import { FeatureFlags } from '@modules/feature-flags/types';

const PatientProfileOverview: FC<PatientProfileOverviewProps> = ({ data, isLoading, isFetching }) => {
  const { t } = useTranslation(GENETIC_COUNSELING_TRANSLATION);
  const { isFeatureEnabled } = useFeatureFlags();
  const isReopenNoShowEnabled = isFeatureEnabled(FeatureFlags.NO_SHOW_REVAMP);
  const getTime = (data: PatientProfileResponse): string => {
    const startTime = data?.appointment?.appointmentStartTime;
    const endTime = data?.appointment?.appointmentEndTime;
    const timezone = data?.appointment?.timeZone;
    if (!startTime || !endTime || !timezone) {
      return '';
    }

    return getAppointmentTime(startTime, endTime, timezone) ?? '';
  };

  const getFormattedDate = (date: string | undefined): string => {
    return date ? formatDate(date) : BLANK_VALUE;
  };

  const getFormattedDateWithTime = (date: string | undefined): string => {
    return date
      ? `${formatDate(date, LARGE_DATE_FORMAT)} ${t('at')} ${new Date(date)
          .toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
          .toLowerCase()}`
      : BLANK_VALUE;
  };

  return (
    <>
      {isLoading && isFetching && <Loader css={{ height: '311px' }} />}
      {!isLoading && data?.status === ReferralStatuses.PENDING && data?.statusDetails?.lastAttemptedAt && (
        <Box css={{ paddingBottom: '$6' }}>
          <PatientContactAttemptedCard
            contactAttemptInfo={{
              contactDate: getFormattedDateWithTime(data?.statusDetails.lastAttemptedAt),
              notes: data?.statusDetails?.declinedReasonNotes,
            }}
          />
        </Box>
      )}
      {!isLoading && data?.status === ReferralStatuses.UPCOMING && (
        <Box css={{ paddingBottom: '$6' }}>
          <UpcomingAppointmentCard
            appointmentInfo={{
              id: data?.appointment?.appointmentID,
              date: getFormattedDate(data?.appointment?.appointmentStartTime),
              time: getTime(data),
              type: data?.appointment?.appointmentType,
            }}
          />
        </Box>
      )}
      {!isLoading && data && isCompletedStatus(data.status) && (
        <Box css={{ paddingBottom: '$6' }}>
          <AppointmentCompletedCard
            appointmentInfo={{
              completionDate: data.appointment?.completionDateTime,
              timezone: data.appointment?.timeZone,
            }}
          />
        </Box>
      )}
      {!isLoading && data?.status === ReferralStatuses.NO_SHOW && (
        <Box css={{ paddingBottom: '$6' }}>
          {isReopenNoShowEnabled ? (
            <PatientNoShowCard
              appointmentInfo={{
                id: data?.appointment?.appointmentID,
                completionDate: data.appointment?.completionDateTime,
                timezone: data.appointment?.timeZone,
              }}
            />
          ) : (
            <AppointmentCompletedCard
              appointmentInfo={{
                completionDate: data.appointment?.completionDateTime,
                timezone: data.appointment?.timeZone,
              }}
            />
          )}
        </Box>
      )}
      {!isLoading && data?.status === ReferralStatuses.DECLINED && (
        <Box css={{ paddingBottom: '$6' }}>
          <PatientDeclinedCard
            appointmentInfo={{
              id: data?.appointment?.appointmentID,
              declinedDate: getFormattedDate(data?.appointment?.declinedDatetimeTime),
              reasonCode: data?.statusDetails?.declinedReasonCode,
              notes: data?.statusDetails?.declinedReasonNotes,
            }}
          />
        </Box>
      )}
    </>
  );
};

export default PatientProfileOverview;
